/**
 * SalesDefaultsSettings — Paramètres pré-remplis pour Ventes.
 * Chaque nouveau devis/facture hérite de ces défauts, éditables par doc.
 */
import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Save, Info, FileText, RefreshCw, Sparkles, Recycle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import AIBillingSetupWizard from "@/components/sales/AIBillingSetupWizard";

const NOTES_PRESETS = [
  { label: "Prestation SaaS", value:
    "Bonjour {client_name}, merci de votre confiance. Le service démarre à réception du règlement sous {delay} jours." },
  { label: "Prestation Conseil", value:
    "Bonjour {client_name}, livrables prévus au bon de commande. Facturation ({number}) à l'issue de la mission — règlement à {delay} jours." },
  { label: "Vente matériel", value:
    "Bonjour {client_name}, garantie constructeur applicable. Retour sous 14 jours à réception. Total TTC : {total_ttc}." },
];

const AVAILABLE_TOKENS = [
  "client_name", "client_email", "number", "issue_date", "due_date",
  "delay", "total_ht", "total_ttc", "total_tva", "seller_name",
  "year", "month", "day",
];

export default function SalesDefaultsSettings({ embedded = false } = {}) {
  const { hasPermission } = useAuth();
  const canWrite = hasPermission("invoices.write");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState("");
  const [wizardOpen, setWizardOpen] = useState(false);
  const [lastExtraction, setLastExtraction] = useState(null);
  const [reusing, setReusing] = useState(false);
  const [form, setForm] = useState({
    notes_template: "", footer_text: "", cgv_url: "",
    default_late_penalty_text: "", default_discount_text: "",
    default_payment_terms_days: 30,
    bank_accounts: [], default_payment_method: null,
  });

  const load = async () => {
    setLoading(true);
    try {
      const [defaultsRes, statusRes] = await Promise.all([
        api.get("/billing/sales-defaults"),
        api.get("/billing/setup-status").catch(() => ({ data: {} })),
      ]);
      const r = defaultsRes;
      setForm({
        notes_template: r.data.notes_template || "",
        footer_text: r.data.footer_text || "",
        cgv_url: r.data.cgv_url || "",
        default_late_penalty_text: r.data.default_late_penalty_text || "",
        default_discount_text: r.data.default_discount_text || "",
        default_payment_terms_days: r.data.default_payment_terms_days || 30,
        bank_accounts: r.data.bank_accounts || [],
        default_payment_method: r.data.default_payment_method || null,
      });
      setLastExtraction(statusRes.data?.last_ai_extraction || null);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const reuseExtraction = async () => {
    setReusing(true);
    try {
      const r = await api.post("/billing/sales-defaults/apply-from-last-extraction",
        { overwrite_existing: false });
      const updated = r.data?.keys_updated || [];
      if (updated.length === 0) {
        toast.info("Aucun champ à mettre à jour — tout est déjà rempli.");
      } else {
        toast.success(`Paramètres réutilisés · ${updated.length} champ(s) rempli(s) sans nouveau call IA`);
      }
      await load();
    } catch (e) { toast.error(apiError(e, "Impossible de réutiliser l'extraction")); }
    finally { setReusing(false); }
  };

  // Preview live des tokens résolus
  useEffect(() => {
    if (!form.notes_template) { setPreview(""); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.post("/billing/notes-preview", { text: form.notes_template });
        setPreview(r.data.preview || "");
      } catch { setPreview(""); }
    }, 300);
    return () => clearTimeout(t);
  }, [form.notes_template]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/billing/sales-defaults", {
        ...form,
        default_payment_terms_days: Number(form.default_payment_terms_days) || 30,
      });
      toast.success("Paramètres Ventes enregistrés");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="h-8 w-72 bg-slate-100 animate-pulse rounded" />;

  return (
    <div className="space-y-6" data-testid="sales-defaults-settings">
      <div>
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
          Paramètres Ventes
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Ces valeurs sont pré-remplies automatiquement à la création de chaque devis / facture.
          Vous pourrez toujours les modifier ligne par ligne pour un document spécifique.
        </p>
      </div>

      {/* CTA IA — extraction automatique depuis facture existante */}
      {canWrite && lastExtraction && (
        <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-teal-50"
              data-testid="reuse-extraction-card">
          <CardContent className="pt-5 pb-5">
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <Recycle className="w-5 h-5 text-emerald-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base font-semibold text-slate-900 mb-1">
                  Réutiliser l'analyse existante
                </h3>
                <p className="text-xs text-slate-600 mb-2">
                  Nous avons déjà analysé{" "}
                  <span className="font-mono font-medium text-slate-900">
                    {lastExtraction.source_filename || lastExtraction.source_number || "votre document"}
                  </span>
                  {lastExtraction.detected_at && (
                    <> le{" "}
                      <span className="font-medium text-slate-900">
                        {new Date(lastExtraction.detected_at).toLocaleDateString("fr-FR", {
                          day: "2-digit", month: "long", year: "numeric",
                        })}
                      </span>
                    </>
                  )}
                  {" "}lors de la configuration de la numérotation. Réutilisez ces
                  données pour pré-remplir vos paramètres Ventes — sans nouveau
                  call IA (aucun token consommé).
                </p>
                <div className="flex flex-wrap gap-2 mt-3">
                  <Button onClick={reuseExtraction} disabled={reusing}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                          data-testid="reuse-extraction-btn">
                    {reusing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                             : <Recycle className="w-4 h-4 mr-2" />}
                    Utiliser ces données
                  </Button>
                  <Button onClick={() => setWizardOpen(true)} variant="outline"
                          className="border-slate-300"
                          data-testid="open-ai-wizard-btn-defaults">
                    <Sparkles className="w-4 h-4 mr-2 text-purple-600" />
                    Importer une autre facture
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* CTA IA — extraction automatique depuis facture existante (première fois) */}
      {canWrite && !lastExtraction && (
        <Card className="border-purple-200 bg-gradient-to-br from-purple-50 via-white to-blue-50">
          <CardContent className="pt-5 pb-5">
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5 text-purple-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-base font-semibold text-slate-900 mb-1">
                  Pré-remplir depuis ma dernière facture
                </h3>
                <p className="text-xs text-slate-600 mb-3">
                  Notre IA extrait automatiquement ton IBAN, ton délai de paiement,
                  tes pénalités de retard et tes mentions légales — plus rien à
                  saisir manuellement.
                </p>
                <Button onClick={() => setWizardOpen(true)}
                         className="bg-purple-600 hover:bg-purple-700 text-white"
                         data-testid="open-ai-wizard-btn-defaults">
                  <Sparkles className="w-4 h-4 mr-2" />
                  Détecter avec l'IA
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Alert className="bg-blue-50 border-blue-200">
        <Info className="w-4 h-4 text-blue-600" />
        <AlertDescription className="text-xs text-blue-900">
          <strong>Astuce :</strong> mettez ici vos <em>mentions générales</em> (CGV, garantie…).
          Dans chaque devis/facture, un champ « Notes » restera libre pour les infos <em>spécifiques au client</em>.
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="w-4 h-4 text-blue-600" /> Notes / Conditions par défaut
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label htmlFor="notes_template" className="text-sm">Modèle de notes (pré-rempli)</Label>
            <Textarea id="notes_template" name="notes_template" rows={4} value={form.notes_template} disabled={!canWrite}
                       onChange={(e) => setForm((p) => ({ ...p, notes_template: e.target.value }))}
                       placeholder="Ex: Bonjour {client_name}, merci de votre confiance. Règlement sous {delay} jours."
                       data-testid="notes-template-input" />
            <div className="flex flex-wrap gap-1.5 mt-2">
              <span className="text-[10px] text-slate-500 uppercase tracking-wider self-center">Presets :</span>
              {NOTES_PRESETS.map((p) => (
                <Button key={p.label} type="button" variant="outline" size="sm" className="text-xs h-7"
                         disabled={!canWrite}
                         onClick={() => setForm((f) => ({ ...f, notes_template: p.value }))}>
                  {p.label}
                </Button>
              ))}
            </div>
            {/* Tokens dispo */}
            <div className="mt-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
              <p className="text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">Tokens dynamiques</p>
              <div className="flex flex-wrap gap-1">
                {AVAILABLE_TOKENS.map((t) => (
                  <button key={t} type="button" onClick={() => {
                    if (!canWrite) return;
                    setForm((f) => ({ ...f, notes_template: (f.notes_template || "") + `{${t}}` }));
                  }}
                    className="text-[11px] font-mono px-1.5 py-0.5 bg-white rounded border border-slate-200 hover:bg-blue-50 hover:border-blue-300 transition"
                    data-testid={`token-${t}`}>
                    {`{${t}}`}
                  </button>
                ))}
              </div>
              {preview && (
                <div className="mt-3 pt-3 border-t border-slate-200">
                  <p className="text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1">Aperçu (données factices)</p>
                  <p className="text-xs text-slate-700 whitespace-pre-wrap" data-testid="notes-preview">{preview}</p>
                </div>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="footer_text" className="text-sm">Mentions bas de page (footer PDF)</Label>
              <Textarea id="footer_text" name="footer_text" rows={2} value={form.footer_text} disabled={!canWrite}
                         onChange={(e) => setForm((p) => ({ ...p, footer_text: e.target.value }))}
                         placeholder="Ex: SARL au capital de 10 000€ – SIRET 123..." />
            </div>
            <div>
              <Label htmlFor="cgv_url" className="text-sm">Lien CGV (optionnel)</Label>
              <Input id="cgv_url" name="cgv_url" value={form.cgv_url} disabled={!canWrite}
                      onChange={(e) => setForm((p) => ({ ...p, cgv_url: e.target.value }))}
                      placeholder="https://votre-site.fr/cgv" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Conditions de règlement</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label htmlFor="default_payment_terms_days" className="text-sm">Délai de paiement (jours)</Label>
              <Input id="default_payment_terms_days" name="default_payment_terms_days" type="number" min="0" max="365" value={form.default_payment_terms_days} disabled={!canWrite}
                      onChange={(e) => setForm((p) => ({ ...p, default_payment_terms_days: e.target.value }))} />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="default_late_penalty_text" className="text-sm">Pénalités de retard (obligatoire loi FR)</Label>
              <Input id="default_late_penalty_text" name="default_late_penalty_text" value={form.default_late_penalty_text} disabled={!canWrite}
                      onChange={(e) => setForm((p) => ({ ...p, default_late_penalty_text: e.target.value }))} />
            </div>
          </div>
          <div>
            <Label htmlFor="default_discount_text" className="text-sm">Escompte</Label>
            <Input id="default_discount_text" name="default_discount_text" value={form.default_discount_text} disabled={!canWrite}
                    onChange={(e) => setForm((p) => ({ ...p, default_discount_text: e.target.value }))} />
          </div>
        </CardContent>
      </Card>

      {/* Sprint 89b — Coordonnées bancaires multi-comptes */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="w-4 h-4 text-emerald-600" />
            Coordonnées bancaires (affichées sur les factures)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-slate-500">
            Configurez ici vos comptes bancaires (multi-comptes possibles). Le compte marqué « par défaut »
            est pré-rempli automatiquement sur chaque nouvelle facture — modifiable au cas par cas.
          </p>
          {(form.bank_accounts || []).map((acc, i) => (
            <div key={acc.id || i}
                  className={`p-3 border rounded-lg space-y-2 ${acc.is_default ? "border-emerald-300 bg-emerald-50/40" : "border-slate-200 bg-white"}`}
                  data-testid={`bank-account-row-${i}`}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <div>
                  <Label htmlFor={`bank-label-${i}`} className="text-[10px] text-slate-500 uppercase">Libellé</Label>
                  <Input id={`bank-label-${i}`} name={`bank-label-${i}`} value={acc.label || ""} disabled={!canWrite}
                          placeholder="Compte pro"
                          onChange={(e) => setForm((f) => {
                            const arr = [...(f.bank_accounts || [])];
                            arr[i] = { ...arr[i], label: e.target.value };
                            return { ...f, bank_accounts: arr };
                          })}
                          data-testid={`bank-label-${i}`} />
                </div>
                <div>
                  <Label htmlFor={`bank-name-${i}`} className="text-[10px] text-slate-500 uppercase">Nom de la banque</Label>
                  <Input id={`bank-name-${i}`} name={`bank-name-${i}`} value={acc.bank_name || ""} disabled={!canWrite}
                          placeholder="Caisse d'Épargne"
                          onChange={(e) => setForm((f) => {
                            const arr = [...(f.bank_accounts || [])];
                            arr[i] = { ...arr[i], bank_name: e.target.value };
                            return { ...f, bank_accounts: arr };
                          })} />
                </div>
                <div>
                  <Label htmlFor={`bank-holder-${i}`} className="text-[10px] text-slate-500 uppercase">Titulaire du compte</Label>
                  <Input id={`bank-holder-${i}`} name={`bank-holder-${i}`} value={acc.account_holder || ""} disabled={!canWrite}
                          placeholder="BE-TAYE SAS"
                          onChange={(e) => setForm((f) => {
                            const arr = [...(f.bank_accounts || [])];
                            arr[i] = { ...arr[i], account_holder: e.target.value };
                            return { ...f, bank_accounts: arr };
                          })} />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr_auto] gap-2 items-end">
                <div>
                  <Label htmlFor={`bank-iban-${i}`} className="text-[10px] text-slate-500 uppercase">IBAN</Label>
                  <Input id={`bank-iban-${i}`} name={`bank-iban-${i}`} value={acc.iban || ""} disabled={!canWrite}
                          placeholder="FR76 3000 6000 0112 3456 7890 189"
                          className="font-mono text-xs"
                          onChange={(e) => setForm((f) => {
                            const arr = [...(f.bank_accounts || [])];
                            arr[i] = { ...arr[i], iban: e.target.value };
                            return { ...f, bank_accounts: arr };
                          })}
                          data-testid={`bank-iban-${i}`} />
                </div>
                <div>
                  <Label htmlFor={`bank-bic-${i}`} className="text-[10px] text-slate-500 uppercase">BIC</Label>
                  <Input id={`bank-bic-${i}`} name={`bank-bic-${i}`} value={acc.bic || ""} disabled={!canWrite}
                          placeholder="CEPAFRPP751"
                          className="font-mono text-xs"
                          onChange={(e) => setForm((f) => {
                            const arr = [...(f.bank_accounts || [])];
                            arr[i] = { ...arr[i], bic: e.target.value };
                            return { ...f, bank_accounts: arr };
                          })} />
                </div>
                <div className="flex gap-2">
                  <Button type="button" variant={acc.is_default ? "default" : "outline"}
                           className={acc.is_default ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}
                           size="sm" disabled={!canWrite}
                           onClick={() => setForm((f) => {
                             const arr = (f.bank_accounts || []).map((a, idx) => ({ ...a, is_default: idx === i }));
                             return { ...f, bank_accounts: arr };
                           })}
                           data-testid={`bank-default-${i}`}>
                    {acc.is_default ? "★ Par défaut" : "Définir par défaut"}
                  </Button>
                  <Button type="button" variant="ghost" size="sm" disabled={!canWrite}
                           className="text-red-600 hover:bg-red-50"
                           onClick={() => setForm((f) => ({
                             ...f, bank_accounts: (f.bank_accounts || []).filter((_, idx) => idx !== i),
                           }))}
                           data-testid={`bank-remove-${i}`}>
                    Retirer
                  </Button>
                </div>
              </div>
            </div>
          ))}
          {(!form.bank_accounts || form.bank_accounts.length === 0) && (
            <p className="text-xs text-slate-400 italic py-2">Aucun compte configuré. Ajoutez-en un pour l'afficher automatiquement sur vos factures.</p>
          )}
          {canWrite && (
            <Button type="button" variant="outline" size="sm"
                     onClick={() => setForm((f) => ({
                       ...f, bank_accounts: [...(f.bank_accounts || []),
                         { label: "", iban: "", bic: "", bank_name: "", account_holder: "",
                           is_default: (f.bank_accounts || []).length === 0 }],
                     }))}
                     data-testid="bank-add-btn">
              + Ajouter un compte bancaire
            </Button>
          )}
          <div className="pt-2 border-t border-slate-100">
            <Label className="text-sm">Mode de paiement préféré</Label>
            <select id="default-payment-method" name="default_payment_method" value={form.default_payment_method || ""} disabled={!canWrite}
                     onChange={(e) => setForm((f) => ({ ...f, default_payment_method: e.target.value || null }))}
                     className="mt-1 w-full h-9 rounded-md border border-slate-200 bg-white px-3 text-sm"
                     data-testid="default-payment-method">
              <option value="">— Aucun (à choisir par facture)</option>
              <option value="virement">Virement bancaire</option>
              <option value="cheque">Chèque</option>
              <option value="cb">Carte bancaire</option>
              <option value="prelevement">Prélèvement SEPA</option>
              <option value="especes">Espèces</option>
              <option value="autre">Autre</option>
            </select>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={load} disabled={saving}>
          <RefreshCw className="w-4 h-4 mr-1.5" /> Recharger
        </Button>
        {canWrite && (
          <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white"
                   data-testid="save-sales-defaults-btn">
            <Save className="w-4 h-4 mr-1.5" />
            {saving ? "Enregistrement..." : "Enregistrer"}
          </Button>
        )}
      </div>

      <AIBillingSetupWizard
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        onDone={() => { setWizardOpen(false); load(); }}
      />
    </div>
  );
}
